import styled from '@emotion/styled';
import Downshift, { type ChildrenFunction } from 'downshift';
import React, { useEffect, useMemo, useState } from 'react';

import { useLayer } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';

import { getDownshiftEnvironment } from '../../utilities/downshift';
import { getTextFromNode } from '../../utilities/text';

import Menu, { MenuItem, menuPositions } from '../menu/menu';
import SearchBar from '../search-bar/search-bar';

type GetInputProps = Parameters<
  ChildrenFunction<Downshift>
>[0]['getInputProps'];

interface GetAccessibilityProps {
  ariaMenuName: string | undefined;
  getInputProps: GetInputProps;
  isOpen: boolean;
}

const getAccessibilityProps = ({
  ariaMenuName,
  getInputProps,
  isOpen,
}: GetAccessibilityProps): {
  'aria-activedescendant': string;
  'aria-expanded': boolean;
  'aria-label': string;
} => {
  return {
    // Ensures all options within the list of selections can be narrated by a screen reader
    'aria-activedescendant': getInputProps()['aria-activedescendant'],

    'aria-expanded': isOpen,

    // Provides a screen-readable label for the dropdown
    'aria-label': ariaMenuName ? ariaMenuName : '',
  };
};

const TriggerSection = styled.div`
  display: inline-block;
  vertical-align: middle;
`;

const Dropdown = ({
  ariaMenuName,
  menuPosition = 'left',
  menuZIndex = 1100,
  options,
  trigger,
  triggerCallback,
  isOpen: isOpenFromProps,
  menuMinWidth = 24,
  menuMaxWidth = 48,
  menuMaxHeight,
  container,
  onOuterClick,
  triggerOffset = 0,
  onOpenChange,
  search,
  role,
  menuItemRole,
  ...props
}: DropdownProps) => {
  const environment = getDownshiftEnvironment(container);

  const [downshiftIsOpen, setDownshiftIsOpen] = useState(false);
  const [keyboardMove, setKeyboardMove] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Enter':
      case ' ':
      case 'Tab':
      case 'Escape':
        setKeyboardMove(true);
        break;
      default:
        break;
    }
  };

  const isOpen = isOpenFromProps || downshiftIsOpen;

  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen,
    container,
    placement: menuPositions[menuPosition],
    ResizeObserver,
    auto: true,
    snap: true,
    triggerOffset,
  });

  useEffect(() => {
    onOpenChange && onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  const stateReducer = (state, changes) => {
    if (changes.isOpen !== undefined) {
      setDownshiftIsOpen(changes.isOpen);
    }

    return changes;
  };

  // Filtering in Atlassian is based on if the searched value is a substring of the provided values at
  // any point of the string.
  // For example is the searched value is "a" and the provided values are "a", "ab", "b", and "ba", the
  // returned values will be "a", "ab", and "ba".
  options = useMemo(() => {
    if (search) {
      if (search.searchType === 'startsWith') {
        return options.filter(option =>
          getTextFromNode(option.title)
            .toLowerCase()
            .startsWith(searchValue.toLowerCase())
        );
      }

      return options.filter(option =>
        getTextFromNode(option.title)
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      // If the search parameter is not provided, return the options as is
    }
    return options;
  }, [options, searchValue, search]);

  return (
    <Downshift
      stateReducer={stateReducer}
      itemToString={item => (item ? item.title : '')}
      onSelect={item =>
        item && !item.disabled && item.onClick && item.onClick()
      }
      onOuterClick={onOuterClick}
      environment={environment}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        isOpen,
      }) => (
        <div
          {...props}
          {...(triggerCallback
            ? {
                // the Downshift component appears to set the following props,
                // which we do not want applied to this element
                role: null,
                'aria-haspopup': null,
                'aria-expanded': null,
                'aria-labelledby': null,
              }
            : {})}
        >
          <div {...triggerProps}>
            {triggerCallback ? (
              triggerCallback({
                ...getToggleButtonProps({ onKeyDown }),
                ...getAccessibilityProps({
                  ariaMenuName,
                  getInputProps,
                  isOpen,
                }),
              })
            ) : (
              <TriggerSection
                {...getToggleButtonProps({ onKeyDown, tabIndex: 0 })}
              >
                {trigger}
              </TriggerSection>
            )}
          </div>
          {isOpen &&
            renderLayer(
              <div
                {...layerProps}
                style={{ ...layerProps.style, zIndex: menuZIndex }}
              >
                <Menu
                  position={menuPosition}
                  minWidth={menuMinWidth}
                  maxWidth={menuMaxWidth}
                  maxHeight={menuMaxHeight}
                  downshiftMenuProps={() => getMenuProps({ onKeyDown })}
                  role={role}
                  search={
                    search && (
                      <SearchBar
                        ariaLabel={search.searchPlaceholder}
                        placeholder={search.searchPlaceholder}
                        value={searchValue}
                        onChange={onSearchChange}
                        getInputProps={getInputProps}
                      />
                    )
                  }
                >
                  {options.map((item, index) => {
                    return (
                      <MenuItem
                        key={index}
                        isHighlighted={highlightedIndex === index}
                        keyboardMove={
                          keyboardMove && highlightedIndex === index
                        }
                        isDisabled={item.disabled}
                        isSelected={item.selected}
                        icon={item.icon}
                        hasDivider={item.hasDivider}
                        getItemProps={getItemProps}
                        menuItemRole={menuItemRole}
                        index={index}
                        {...getItemProps({
                          key: index,
                          index,
                          item,
                          disabled: item.disabled,
                          onMouseMove: () => {
                            if (keyboardMove) {
                              setKeyboardMove(false);
                            }
                          },
                        })}
                      >
                        {item.title}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </div>
            )}
        </div>
      )}
    </Downshift>
  );
};

type OptionsObject = {
  key?: string;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: React.ReactEventHandler;
  disabled?: boolean;
  selected?: boolean;
  hasDivider?: boolean;
};

type SearchProps = {
  searchPlaceholder: string;
  searchType?: 'contains' | 'startsWith';
};

type TriggerProps =
  | {
      /** @deprecated Use triggerCallback and possibly with ariaMenuName for better accessibility support */
      trigger: React.ReactNode;
      triggerCallback?: never;
    }
  | {
      trigger?: never;
      triggerCallback: (buttonProps) => React.ReactNode;
    };

type DropdownProps = TriggerProps & {
  ariaMenuName?: string;
  // TODO: Update the menuPositions to match Tooltip placement Props
  menuPosition?:
    | 'left'
    | 'right'
    | 'topRight'
    | 'topLeft'
    | 'leftSide'
    | 'rightSide';
  options?: OptionsObject[];
  isOpen?: boolean;
  menuZIndex?: number;
  menuMinWidth?: number | string;
  menuMaxWidth?: number | string;
  menuMaxHeight?: number | string;
  container?: HTMLElement | (() => HTMLElement) | string;
  onOuterClick?: () => void;
  triggerOffset?: number;
  onOpenChange?: (isOpen: boolean) => void;
  search?: SearchProps;
  role?: React.AriaRole;
  menuItemRole?: React.AriaRole;
};

export default Dropdown;
